import { ART_PAL, fade, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Tile match scene (placeholder until the raster of docs/art-codex-task-puzzles.md): a quiet tea-house study — jade
 * dusk (a warm paper room in light), a soft lamp pool over the middle where the board lies, and at both edges a few
 * stacked ivory tiles seen from the side and a bamboo stem. The centre stays empty: the board is the picture. Motifs
 * are marked `data-motif="<x>"` (tests keep them out of the middle 40%). No filters, no text, no glyphs on the tiles.
 */
export function tileMatchScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `stm${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const top = P.dark ? mix(P.bg, P.teal, 0.1) : mix(P.green, '#FFFFFF', 0.86);
  const mid = P.dark ? mix(P.bg, P.green, 0.07) : mix(P.gold, '#FFFFFF', 0.88);
  const low = P.dark ? mix(P.bg, P.teal, 0.04) : mix(P.teal, '#FFFFFF', 0.9);
  const ivory = P.dark ? '#E9DFC8' : '#FBF4E4';
  const side = P.dark ? mix(P.bg, P.teal, 0.45) : tint(P.teal, 0.25);
  const bamboo = P.dark ? mix(P.bg, P.green, 0.4) : shade(P.green, 0.05);

  const defs =
    `<defs>${vgrad(`${p}bg`, top, mid, low)}` +
    fade(`${p}lamp`, P.dark ? P.gold : P.orange, P.dark ? 0.12 : 0.14) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.28 : 0.4) +
    `</defs>`;

  /** a small stack of tiles lying flat: ivory tops over jade backs */
  const stack = (x: number, y: number, s: number, count: number) => {
    let g = `<g data-motif="${Math.round(x)}" opacity="${P.dark ? 0.55 : 0.7}">`;
    for (let i = 0; i < count; i++) {
      const yy = y - i * 13 * s;
      const dx = (i % 2 ? 6 : -4) * s;
      g +=
        `<rect x="${n(x + dx - 40 * s)}" y="${n(yy)}" width="${n(80 * s)}" height="${n(13 * s)}" rx="${n(3 * s)}" fill="${side}"/>` +
        `<rect x="${n(x + dx - 40 * s)}" y="${n(yy)}" width="${n(80 * s)}" height="${n(7 * s)}" rx="${n(3 * s)}" fill="${ivory}"/>`;
    }
    return g + '</g>';
  };
  /** a bamboo stem with two leaves */
  const stem = (x: number, y0: number, y1: number, s: number, flip: 1 | -1) => {
    let g = `<g data-motif="${Math.round(x)}" opacity="${P.dark ? 0.5 : 0.6}"><rect x="${n(x - 5 * s)}" y="${y0}" width="${n(10 * s)}" height="${y1 - y0}" rx="${n(5 * s)}" fill="${bamboo}"/>`;
    for (let yy = y0 + 70; yy < y1; yy += 110)
      g += `<rect x="${n(x - 7 * s)}" y="${yy}" width="${n(14 * s)}" height="${n(4 * s)}" rx="2" fill="${shade(bamboo, 0.25)}"/>`;
    const lx = x + 6 * s * flip;
    g += `<path d="M${n(lx)} ${y0 + 90}q${n(60 * s * flip)} ${n(-14 * s)} ${n(110 * s * flip)} ${n(12 * s)}q${n(-60 * s * flip)} ${n(10 * s)} ${n(-110 * s * flip)} ${n(-12 * s)}z" fill="${bamboo}"/>`;
    g += `<path d="M${n(lx)} ${y0 + 200}q${n(50 * s * flip)} ${n(-4 * s)} ${n(90 * s * flip)} ${n(24 * s)}q${n(-52 * s * flip)} ${n(2 * s)} ${n(-90 * s * flip)} ${n(-24 * s)}z" fill="${bamboo}"/>`;
    return g + '</g>';
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.42)}" rx="${n(W * 0.42)}" ry="${n(H * 0.4)}" fill="url(#${p}lamp)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.48)}" rx="${n(W * 0.3)}" ry="${n(H * 0.38)}" fill="url(#${p}calm)"/>`;
  if (wide)
    art +=
      stem(140, 120, 900, 1.2, 1) +
      stack(300, 780, 1.3, 4) +
      stem(1470, 160, 900, 1.1, -1) +
      stack(1290, 800, 1.2, 3);
  else art += stack(24, 790, 0.6, 3) + stack(W - 20, 795, 0.6, 2);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">${defs}${art}</svg>`;
}

/** Header accent of Tile match: pairs of tiny ivory tiles on a jade line (repeat-x tile, 40 × 8). */
export function tileMatchAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const jade = P.dark ? shade(P.teal, 0.1) : P.teal;
  const ivory = P.dark ? '#E9DFC8' : '#F3E8CF';
  const tile = (x: number) =>
    `<rect x="${x}" y="1" width="5" height="6" rx="1" fill="${ivory}" stroke="${jade}" stroke-width=".8"/>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 8" width="40" height="8"><rect y="3.5" width="40" height="1" fill="${jade}" opacity=".6"/>${tile(4)}${tile(10)}${tile(24)}${tile(30)}</svg>`;
}

export default defineScene({ game: 'tile-match', scene: tileMatchScene, accent: tileMatchAccent });
